import { EDecision, EPlanType } from '@if/core/types'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchedLoanConditionsDto } from '../../../data/api/swap-applicants-roles/SwitchedLoanConditionsDto'
import { useSwapApplicantsSimulation } from '../../../data/api/swap-applicants-roles/swap-applicants-roles-api'
import { useCurrentLang, usePlanEnumForProgram } from '../../../data/selectors/app-config-selectors'
import { useMerchantPaymentPlan } from '../../../data/selectors/credit-selectors'
import { CreditApplication, PersonalLoanWorksheet } from '../../../data/types'
import { TranslatedEnum } from '../../../data/types/TranslatedEnum'
import { Constants } from '../../../data/types/constants'
import LoanConditions from './LoanConditions'

interface SwapApplicantsSimulationDialogProps {
  open: boolean
  onClose: () => void
  onApply: () => Promise<void>
  disabled: boolean
  creditApplication: CreditApplication
  isApplying?: boolean
}

const planTypeStars: Record<EPlanType, number> = {
  [EPlanType.AutoGold]: 5,
  [EPlanType.AutoSilver]: 4,
  [EPlanType.AutoBronze]: 3,
  [EPlanType.AutoGoldFS]: 5,
  [EPlanType.AutoBronzeFS]: 3,
  [EPlanType.OneStar]: 1,
  [EPlanType.TwoStar]: 2,
  [EPlanType.ThreeStar]: 3,
  [EPlanType.FourStar]: 4,
  [EPlanType.FiveStar]: 5,
  [EPlanType.CreditRslaGold]: 5,
  [EPlanType.CreditRslaSilver]: 4,
  [EPlanType.CreditRslaBronze]: 3,
  [EPlanType.B2CGold]: 5,
  [EPlanType.B2CSilver]: 4,
  [EPlanType.B2CBronze]: 3,
  [EPlanType.Default]: 0,
}

const getDisplayInterestRate = (rawRate: number | undefined | null, maxRate: number): number =>
  rawRate && rawRate > 0 ? rawRate : maxRate

const SwapApplicantsSimulationDialog: React.FC<SwapApplicantsSimulationDialogProps> = ({
  open,
  onClose,
  onApply,
  disabled,
  creditApplication,
  isApplying,
}) => {
  const { t } = useTranslation()
  const {
    data: swapSimulation,
    isLoading: swapLoading,
    error: swapError,
  } = useSwapApplicantsSimulation(creditApplication.financingProgramId, creditApplication.id, open)

  const { worksheet } = useCreditApplicationData()
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined

  const planEnum = usePlanEnumForProgram(creditApplication.financingProgramId) as Record<EPlanType, TranslatedEnum>
  const lang = useCurrentLang()

  const merchantPaymentPlan = useMerchantPaymentPlan(creditApplication, personalLoanWorksheet)
  const maxInterestRate = Constants.financingProgramConfigs[creditApplication.financingProgramId].maxInterestRate

  const currentInterestRate = getDisplayInterestRate(
    creditApplication.finalCreditDecision?.interestRate,
    maxInterestRate,
  )
  const swapInterestRate = swapSimulation
    ? getDisplayInterestRate(swapSimulation.interestRate, maxInterestRate)
    : maxInterestRate

  const curentLoanConditions: SwitchedLoanConditionsDto = {
    creditApplicationId: creditApplication.id,
    interestRate: currentInterestRate,
    maxLoanAmount: creditApplication.finalCreditDecision?.maxAmountFinanced ?? 0,
    maxMonthlyPayment: creditApplication.finalCreditDecision?.maxPmtAmount ?? 0,
    maxTermDuration: creditApplication.finalCreditDecision?.maxTermDuration ?? 0,
    planName: creditApplication.normsCreditDecision?.planName ?? EPlanType.Default,
    merchantPaymentPlan: merchantPaymentPlan ?? undefined,
    decision: creditApplication.finalCreditDecision?.decision ?? EDecision.Refused,
  }

  const swapSimulationLoanConditions: SwitchedLoanConditionsDto | undefined = swapSimulation
    ? {
        ...swapSimulation,
        interestRate: swapInterestRate,
        planName: swapSimulation.planName ?? EPlanType.Default,
      }
    : undefined

  const currentPlanStars = getPlanStars(curentLoanConditions.planName)
  const swapPlanStars = getPlanStars(swapSimulationLoanConditions?.planName)

  const isSimulatedBetter = swapSimulationLoanConditions
    ? isSimulatedLoanBetter(currentInterestRate, swapInterestRate, currentPlanStars, swapPlanStars)
    : false

  const isDeclinedBefore = creditApplication.finalCreditDecision?.decision === EDecision.Refused
  const isDeclinedAfter = swapSimulation?.decision === EDecision.Refused
  const isSwapSimulationBetter = swapSimulation && swapInterestRate < currentInterestRate

  function getPlanStars(plan: EPlanType | undefined): number {
    return plan ? planTypeStars[plan] : 0
  }

  function isSimulatedLoanBetter(
    currentRate: number,
    simulatedRate: number,
    currentStars: number,
    simulatedStars: number,
  ): boolean {
    return simulatedRate < currentRate && simulatedStars >= currentStars
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('credit.swapSimulationTitle', 'Simulated Role Swap Comparison', {
          applicantFirstName: creditApplication.applicant.firstName,
          coApplicantFirstName: creditApplication.coApplicant?.firstName || '',
        })}
        <IconButton aria-label={t('common.close')} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {swapLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 120 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, textAlign: 'center' }}>
              {t('credit.swapSimulationStatusMessage', 'The simulation is in progress....')}
            </Typography>
          </Stack>
        ) : swapSimulation ? (
            <Stack>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="center"
                alignItems="stretch"
                sx={{ width: '100%', mb: 2 }}
              >
                <LoanConditions
                  label={t('credit.beforeSwitch', 'Before Role Swap – {{firstName}}', {
                    firstName: creditApplication.applicant.firstName,
                  })}
                  loanConditionsDto={curentLoanConditions}
                  planEnum={planEnum}
                  lang={lang}
                  isBetter={!isSimulatedBetter && !isDeclinedBefore}
                />
                {swapSimulationLoanConditions && (
                  <LoanConditions
                    label={t('credit.afterSwitch', 'After Role Swap – {{firstName}}', {
                      firstName: creditApplication.coApplicant?.firstName,
                    })}
                    loanConditionsDto={swapSimulationLoanConditions}
                    planEnum={planEnum}
                    lang={lang}
                    isBetter={isSimulatedBetter && !isDeclinedAfter}
                  />
                )}
              </Stack>
              {
                swapSimulationLoanConditions?.decision === EDecision.Refused && (
                  <Stack direction="row" spacing={1}>
                    {swapSimulationLoanConditions?.refusalReasonCodes?.map(code => <Chip key={code} label={t(`CreditDecisionNorms.${code}`, code)} variant="outlined" />)}
                  </Stack>
                )
              }
            </Stack>
        ) : swapError ? (
          <Typography color="error" sx={{ textAlign: 'center', mt: 2 }}>
            {t('error', 'Error running the simulation')}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={onApply}
          disabled={disabled || swapLoading || !swapSimulation || isApplying || (isDeclinedBefore && isDeclinedAfter)}
          variant="contained"
          color={isSwapSimulationBetter ? 'success' : 'primary'}
          loading={isApplying}
          loadingPosition="start"
          startIcon={isSwapSimulationBetter ? <CheckCircleIcon /> : undefined}
        >
          {isApplying ? t('credit.applyingSimulation', 'Applying...') : t('credit.applySimulation', 'Apply Simulation')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SwapApplicantsSimulationDialog
